import angular, { IPromise } from 'angular'
import { ApiRetailer } from '@vend/types'
import * as retailerResource from '../../components/retailer/retailer-resource/retailer-resource'
import { $q } from '../../ng-compat/imports'
import { EntityStore } from './entity-store'

const RETAILER_SETTINGS_KEY = 'RETAILER_SETTINGS'

export class RetailerSettingsStore extends EntityStore<'settings'> {
  constructor() {
    'ngInject'
    super({
      entityName: 'retailer',
      entityStoreName: 'retailerSettings',
      databaseStoreName: 'settings',
    })
  }

  /**
   * Get the retailer's settings from the IndexedDB store.
   *
   * @return A promise resolved with the retailer's settings
   */
  public get(): IPromise<ApiRetailer> {
    return super.get(RETAILER_SETTINGS_KEY)
  }

  /**
   * Implement the `requestSyncData` hook (see `versionedEntitySyncManager#_requestEntityData`) to handle fetching and
   * versioning the retailer data manually as the API payload differs from all the other versioned entities.
   *
   * @return A promise resolved with the latest retailer settings and version information from the server
   */
  public requestSyncData(/* entitySyncConfig */) {
    return $q
      .all([retailerResource.get(), this.get()])
      .then(([latestSettings, currentSettings]) => {
        const currentVersion = currentSettings && currentSettings.version
        const data = []
        let version = null

        if (latestSettings.version !== currentVersion) {
          data.push(latestSettings)
          version = latestSettings.version
        }

        return {
          data,
          version: {
            min: version,
            max: version,
          },
        }
      })
  }

  /**
   * Sync the given retailer settings data to the IndexedDB store.
   *
   * @param  data
   *         The retailer's settings as returned by the /api/2.0/retailer API end point (in an array of length one).
   *
   * @return A promise resolved once the given retailer settings data has been synced with the
   *         IndexedDB store
   */
  public syncData(data: any[]) {
    const retailerSettings = data && data[0]
    return this.dbStore.put(RETAILER_SETTINGS_KEY, retailerSettings).then(() =>
      this.emitSyncEvent({
        removed: [],
        updated: retailerSettings,
      })
    )
  }
}

export const retailerSettingsStore = new RetailerSettingsStore()

angular
  .module('webregister.factories')
  .constant('retailerSettingsStore', retailerSettingsStore)
